/**
 * pedagogySlice — suivi pédagogique des concepts découverts par l'apprenant.
 *
 * 11/02/2026 (Phase 2 — Pédagogie apprenant).
 *
 * State :
 *   discoveredConcepts: { [conceptId]: { discoveredAt: totalDays, viewed: bool } }
 *   pendingConceptToasts: [ conceptId, ... ]  — file d'attente pour l'UI toast
 *
 * Actions :
 *   evaluatePedagogyConcepts() → à appeler chaque nextDay(). Détecte les
 *                                 nouveaux concepts découverts, les stocke,
 *                                 pousse les toasts dans la file.
 *   markConceptToastShown(conceptId) → l'UI appelle après affichage du toast.
 *   markConceptViewed(conceptId) → si l'apprenant clique sur le détail.
 *
 * Design volontairement minimal — pas de message dans l'inbox (contrairement
 * aux achievements). Le toast in-app + la carte dans « Bilan pédagogique »
 * suffisent pour ne pas surcharger la messagerie.
 */
import { PEDAGOGY_CONCEPTS } from '../../data/pedagogyConcepts';

export const createPedagogySlice = (set, get) => ({
  discoveredConcepts: {},
  pendingConceptToasts: [],

  evaluatePedagogyConcepts: () => {
    const state = get();
    const already = state.discoveredConcepts || {};
    const today = state.totalDays || 0;
    const newlyDiscovered = [];

    for (const concept of PEDAGOGY_CONCEPTS) {
      if (already[concept.id]) continue;
      let ok = false;
      try {
        ok = concept.checkFn(state);
      } catch (err) {
        // Défensif — un concept mal formé ne doit jamais bloquer la
        // partie. On saute silencieusement.
        console.warn('[pedagogySlice] checkFn failed for concept:', concept.id, err);
        continue;
      }
      if (ok) newlyDiscovered.push(concept);
    }

    if (newlyDiscovered.length === 0) return { discovered: [] };

    const updates = { ...already };
    const pending = [...(state.pendingConceptToasts || [])];
    for (const c of newlyDiscovered) {
      updates[c.id] = { discoveredAt: today, viewed: false };
      pending.push(c.id);
    }
    set({ discoveredConcepts: updates, pendingConceptToasts: pending });
    return { discovered: newlyDiscovered };
  },

  markConceptToastShown: (conceptId) => {
    const pending = get().pendingConceptToasts || [];
    set({ pendingConceptToasts: pending.filter((id) => id !== conceptId) });
  },

  markConceptViewed: (conceptId) => {
    const discovered = get().discoveredConcepts || {};
    if (!discovered[conceptId]) return;
    set({
      discoveredConcepts: {
        ...discovered,
        [conceptId]: { ...discovered[conceptId], viewed: true },
      },
    });
  },
});
